// src/components/ListingOfferCard/ListingOfferCard.js
import React from 'react';
import { string, func, bool, oneOfType } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { NamedLink, ResponsiveImage } from '../../components';

import css from './ListingOfferCard.module.css';
import { getServices } from '../../config/requests';

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingRequestCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingRequestCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) return null;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingRequestCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

// util: primer string no vacío y distinto de "recruitment" o "borrador/draft"
const pickFirst = (...vals) =>
  vals.find(v => {
    if (typeof v !== 'string') return false;
    const s = v.trim();
    if (!s) return false;
    if (/^(recruitment|borrador|draft)$/i.test(s)) return false;
    return true;
  }) || '';

// helpers para título/rol
const looksLikeDraft = s => /^(borrador|draft)$/i.test(String(s || '').trim());
const humanize = s =>
  String(s || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase()); // "developer" -> "Developer"

export const ListingOfferCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    isSuggestion,
    isManageMode,
    manageActions,
    titleOverride,
  } = props;

  const SERVICES = getServices(intl) || [];
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;

  const a = currentListing.attributes || {};
  const { title: rawTitleProp = '', publicData = {}, description } = a;

  // ==== Precio / tarifa =======================================================
  const currency = a?.price?.currency || publicData?.currency || 'USD';
  const priceAmount = a?.price?.amount;
  const priceCurrency = a?.price?.currency || currency;
  const hourlyRate =
    publicData?.hourlySalaryRange ||
    publicData?.fee ||
    (priceAmount ? priceAmount / 100 : null);

  const isRecruitment = publicData?.service === 'recruitment';

  const classes = classNames(rootClassName || css.root, className, css.card);

  // ==== ROL (lee 'rol' y 'role', con label desde configs) =====================
  const authorPD = listing?.author?.attributes?.profile?.publicData || {};
  const authorDisplay = listing?.author?.attributes?.profile?.displayName || '';

  const roleRaw =
    publicData?.rol ??
    publicData?.role ??
    authorPD?.rol ??
    authorPD?.role ??
    '';

  const listingFields = (config?.listing?.listingFields || []);
  const roleFieldConfig =
    listingFields.find(f => f.key === 'role') ||
    listingFields.find(f => f.key === 'rol');

  const normalizedRole = String(roleRaw || '').toLowerCase();
  const roleOptions = roleFieldConfig?.enumOptions || [];
  const roleLabel =
    roleOptions.find(o => String(o.option).toLowerCase() === normalizedRole)?.label || null;

  const roleFromPD = humanize(roleRaw);

  // ==== Otros labels auxiliares ==============================================
  const serviceLabel = (() => {
    const s = SERVICES.find(x => x.option === publicData?.service);
    return s?.label_card || s?.label || '';
  })();

  // ==== Título (prioriza el rol) =============================================
  const chosenTitle = pickFirst(
    roleLabel,             // 1) etiqueta del rol desde config
    roleFromPD,            // 2) fallback: rol “humanizado” desde PD
    titleOverride,         // 3) override desde la página
    publicData?.nombrePerfil,
    publicData?.nombre_perfil,
    publicData?.profileName,
    publicData?.perfilNombre,
    publicData?.name,
    publicData?.title,
    authorPD?.nombrePerfil,
    authorPD?.profileName,
    authorPD?.perfilNombre,
    !looksLikeDraft(rawTitleProp) ? rawTitleProp : '',
    authorDisplay,
    serviceLabel
  );

  const finalTitle = chosenTitle || 'Perfil sin título';
  const slug = createSlug(finalTitle);

  // === DEBUG solo en /s ===
  const loggedOnceRef = React.useRef(false);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.location.pathname.startsWith('/s')) return;
    if (loggedOnceRef.current) return;
    loggedOnceRef.current = true;

    try {
      console.groupCollapsed(
        `%c[S] ListingOfferCard`,
        'color:#5e17eb;font-weight:bold',
        `id: ${id}`,
        `title: "${finalTitle}"`
      );
      console.log('attributes.title (rawTitleProp):', rawTitleProp);
      console.log('publicData:', publicData);
      console.log('author publicData:', authorPD);
      console.log('author displayName:', authorDisplay);
      console.log('role raw:', roleRaw);
      console.log('roleLabel:', roleLabel);
      console.log('roleFromPD:', roleFromPD);
      console.log('serviceLabel:', serviceLabel);
      console.groupEnd();
    } catch (e) {
      console.warn('Debug ListingOfferCard error:', e);
    }
  }, [
    id,
    finalTitle,
    rawTitleProp,
    publicData,
    authorPD,
    authorDisplay,
    roleRaw,
    roleLabel,
    roleFromPD,
    serviceLabel,
  ]);

  // ==== Tasks (opcional) ======================================================
  const tasksTotalHours = publicData?.tasks?.reduce((acc, task) => {
    const splitTask = task.split('-');
    const hours = parseInt(splitTask?.[0], 10);
    return acc + (Number.isFinite(hours) ? hours : 0);
  }, 0);

  return (
    <div
      className={classes}
      data-rolecard={
        /consult/i.test(String(roleRaw || ''))
          ? 'role-consultant'
          : 'role-company'
      }
    >
      <div className={css.cardHeader}>
        <div className={css.company}>
          <span className={css.icon}>◈</span>
          {authorPD?.businessName || ''}
        </div>
      </div>

      <div className={css.cardBody}>
        <div className={css.title}>{finalTitle}</div>

        {a?.description ? (
          <div
            style={{
              color: '#4a5568',
              marginBottom: 12,
              fontSize: '0.97rem',
              background: '#f8fafc',
              padding: 10,
              borderRadius: 6,
            }}
          >
            {a.description}
          </div>
        ) : null}

        {publicData?.description && <div className={css.description}>{publicData.description}</div>}

        <div className={css.details}>
          {tasksTotalHours ? (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.tasksTotalHours}>
                <b>No. de horas: </b>
                {tasksTotalHours} horas
              </span>
            </div>
          ) : null}

          {publicData?.modality ? (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.modality}>
                <b>Modalidad de trabajo: </b>
                {publicData?.modality === 'in-person'
                  ? 'Presencial'
                  : publicData?.modality === 'hybrid'
                  ? 'Híbrido'
                  : 'Remoto'}
              </span>
            </div>
          ) : null}

          {publicData?.typeOfContract ? (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.typeOfContract}>
                <b>Tipo de contrato: </b>
                {publicData?.typeOfContract === 'full-time'
                  ? 'Tiempo completo'
                  : publicData?.typeOfContract === 'part-time'
                  ? 'Medio tiempo'
                  : 'Independiente'}
              </span>
            </div>
          ) : null}

          {publicData?.requestLocation?.selectedPlace?.address ? (
            <div className={css.cell}>
              <span className={css.icon}>◈</span>
              <span className={css.location}>
                <b>Ubicación: </b>
                {publicData.requestLocation.selectedPlace.address}
              </span>
            </div>
          ) : null}

          {/* Ocultar "Tarifa" SOLO si es recruitment */}
          {!isRecruitment && (publicData?.budget || hourlyRate) ? (
            <div className={css.cellCol}>
              <span className={css.icon}>◈</span>
              <span className={css.salary}>
                <b>Tarifa: </b>
              </span>
              <span className={css.salary}>
                <span className={css.salaryCurrency}>{priceCurrency}</span>
                <span className={css.salaryAmount}>
                  {publicData?.budget ? `$${publicData.budget}` : hourlyRate ? `$${hourlyRate}` : ''}
                </span>
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {!isManageMode && (
        <div className={css.cardFooter}>
          <NamedLink className={css.applyBtn} name="ListingPage" params={{ id, slug }}>
            Solicita
          </NamedLink>
        </div>
      )}
      {isManageMode && manageActions}
    </div>
  );
};

ListingOfferCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  isSuggestion: false,
  isManageMode: false,
  manageActions: null,
  titleOverride: null,
};

ListingOfferCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  showAuthorInfo: bool,
  isSuggestion: bool,
  renderSizes: string,
  setActiveListing: func,
  isManageMode: bool,
  manageActions: string,
  titleOverride: string,
};

export default injectIntl(ListingOfferCardComponent);
